import { post, put, del } from "@axios";
import { requests } from "shared/requests";
import { AUTH_LOGOUT } from "actions/auth";
import { INITIAL_SYNC_PRODUCTS } from "actions/sync-products";
import { INITIATE_STORES } from "actions/stores";
import { normalizeCartResponse } from "pages/dtf-transfers/by-size/helpers";

const SET_CART = "SET_CART";
const SET_DTF_CART_LOADING = "SET_DTF_CART_LOADING";
const SET_DTF_CART_ERROR = "SET_DTF_CART_ERROR";
const CLEAR_DTF_CART = "CLEAR_DTF_CART";

const setCart = (cart) => {
  const { items, count } = normalizeCartResponse(cart);

  return {
    type: SET_CART,
    items,
    count,
  };
};

const setDtfCartLoading = (loading) => ({
  type: SET_DTF_CART_LOADING,
  loading,
});

const setDtfCartError = (error) => ({
  type: SET_DTF_CART_ERROR,
  error,
});

const clearDtfCart = () => ({
  type: CLEAR_DTF_CART,
});

const handleFailedToken = (dispatch, response) => {
  if (response?.token === "failed") {
    dispatch({ type: INITIATE_STORES });
    dispatch({ type: AUTH_LOGOUT });
    dispatch({ type: INITIAL_SYNC_PRODUCTS });
    return true;
  }

  return false;
};

const loadCart = async (dispatch, emailid, token) => {
  const response = await post({
    url: requests.addToCart,
    data: { user: emailid },
    token,
  });

  if (handleFailedToken(dispatch, response)) {
    return { error: true, message: "Session expired" };
  }

  dispatch(setCart(response));
  return response;
};

const fetchCart = (emailid, token) => {
  return async (dispatch) => {
    if (!emailid) return { error: true, message: "User not signed in" };

    dispatch(setDtfCartLoading(true));

    try {
      return await loadCart(dispatch, emailid, token);
    } catch (error) {
      dispatch(setDtfCartError(error.message));
      return { error: true, message: error.message };
    } finally {
      dispatch(setDtfCartLoading(false));
    }
  };
};

const addCart = (emailid, token, item) => {
  return async (dispatch) => {
    if (!emailid) return { error: true, message: "User not signed in" };

    dispatch(setDtfCartLoading(true));

    try {
      const response = await put({
        url: requests.addToCart,
        data: { user: emailid, item },
        token,
      });

      if (handleFailedToken(dispatch, response)) {
        return { error: true, message: "Session expired" };
      }

      if (response?.error || response?.success === false) {
        return {
          error: true,
          message:
            response?.message ||
            response?.error ||
            "Failed to add item to cart.",
        };
      }

      return await loadCart(dispatch, emailid, token);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to add item to cart.";
      dispatch(setDtfCartError(message));
      return { error: true, message };
    } finally {
      dispatch(setDtfCartLoading(false));
    }
  };
};

const updateCartQuantity = (emailid, token, { cartId, item }) => {
  return async (dispatch) => {
    if (!emailid) return { error: true, message: "User not signed in" };

    dispatch(setDtfCartLoading(true));

    try {
      const response = await put({
        url: requests.addToCart,
        data: {
          user: emailid,
          cartId,
          item,
        },
        token,
      });

      if (handleFailedToken(dispatch, response)) {
        return { error: true, message: "Session expired" };
      }

      return await loadCart(dispatch, emailid, token);
    } catch (error) {
      dispatch(setDtfCartError(error.message));
      return { error: true, message: error.message };
    } finally {
      dispatch(setDtfCartLoading(false));
    }
  };
};

const clearCart = (emailid, token, items) => {
  return async (dispatch, getState) => {
    if (!emailid) return { error: true, message: "User not signed in" };

    const cartItems = items ?? getState()?.dtfCart?.items ?? [];
    if (!cartItems.length) {
      dispatch(clearDtfCart());
      return { success: true };
    }

    dispatch(setDtfCartLoading(true));

    try {
      await Promise.all(
        cartItems.map((item) =>
          del({
            url: requests.addToCart,
            data: {
              user: emailid,
              cartId: item.cartId,
              _id: item.cartRecordId ?? item.id,
            },
            token,
          }),
        ),
      );

      dispatch(clearDtfCart());
      return { success: true };
    } catch (error) {
      dispatch(setDtfCartError(error.message));
      return { error: true, message: error.message };
    } finally {
      dispatch(setDtfCartLoading(false));
    }
  };
};

const removeCartItem = (emailid, token, { cartId, cartRecordId }) => {
  return async (dispatch) => {
    if (!emailid) return { error: true, message: "User not signed in" };

    dispatch(setDtfCartLoading(true));

    try {
      const response = await del({
        url: requests.addToCart,
        data: {
          user: emailid,
          cartId,
          _id: cartRecordId,
        },
        token,
      });

      if (handleFailedToken(dispatch, response)) {
        return { error: true, message: "Session expired" };
      }

      return await loadCart(dispatch, emailid, token);
    } catch (error) {
      dispatch(setDtfCartError(error.message));
      return { error: true, message: error.message };
    } finally {
      dispatch(setDtfCartLoading(false));
    }
  };
};

export {
  SET_CART,
  SET_DTF_CART_LOADING,
  SET_DTF_CART_ERROR,
  CLEAR_DTF_CART,
  fetchCart,
  addCart,
  updateCartQuantity,
  removeCartItem,
  clearCart,
  clearDtfCart,
};
